import { array } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { intersperse } from '@mews/fp';
import {
    Dash,
    EnterpriseLink,
    LocalizedDateFromUtc,
    LocalizedDateTimeFromUtc,
    LocalizedShortTimeFromUtc,
    LocalizedTypography,
    SpacedComma,
    ConvertedCurrencyValue,
} from '@mews-commander/ui';
import {
    AccountType,
    DccPaymentReceiptData,
    PaymentDetails,
    PayworksReceiptData,
    Receipt,
    ReceiptType,
    StripeReceiptData,
} from '@mews-commander/data-access';
import {
    LocalizedCurrencyValue,
    LocalizedEnum,
    LocalizedText,
    LocalizedPercentage,
    DecimalService,
} from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Divider, Typography } from '@optimus-web/core';
import { UnicodeEntity } from '@mews/enums';
import { CompanyDetailTabs } from '../../../../companyDetail/enums';
import { PaymentDetailsCustomEntry, PaymentDetailsEntry, PaymentDetailsLocalizedEntry } from './PaymentDetailsEntry';
import { DccPaymentDetailsExchangeRateEntry } from './DccPaymentDetailsExchangeRateEntry';

const PayerProfileLink = ({
    originalPayer,
    originalPayerId,
    originalPayerAccountType,
}: Pick<PaymentDetails, 'originalPayer' | 'originalPayerId' | 'originalPayerAccountType'>) => {
    if (originalPayerAccountType === AccountType.Company) {
        return (
            <EnterpriseLink
                inherit
                to="Company/Detail/$id"
                params={{ id: originalPayerId }}
                search={{ tab: CompanyDetailTabs.Details }}
            >
                {originalPayer}
            </EnterpriseLink>
        );
    }

    return (
        <EnterpriseLink inherit to="Customer/Detail/$id" params={{ id: originalPayerId }}>
            {originalPayer}
        </EnterpriseLink>
    );
};

export const getPaymentDetailsEntries = (details: PaymentDetails) => {
    const {
        originalPayer,
        originalPayerId,
        originalPayerAccountType,
        createdUtc,
        status,
        amount,
        acquirerReferenceNumber,
        serviceOrderNumber,
        dccPaymentDetail,
        linkedInvoices,
    } = details;

    return [
        {
            textKey: TextKey.Customer,
            value: (
                <PayerProfileLink
                    originalPayer={originalPayer}
                    originalPayerId={originalPayerId}
                    originalPayerAccountType={originalPayerAccountType}
                />
            ),
        },
        {
            textKey: TextKey.Created,
            value: createdUtc ? <LocalizedDateTimeFromUtc utcString={createdUtc} /> : <Dash />,
        },
        {
            textKey: TextKey.Status,
            value: <LocalizedEnum prefix="PaymentState" value={status} />,
        },
        ...(!dccPaymentDetail
            ? [
                  {
                      textKey: TextKey.Amount,
                      value: <LocalizedCurrencyValue value={{ currency: amount.currency, value: amount.value }} />,
                  },
              ]
            : []),
        ...(linkedInvoices.length > 0
            ? [
                  {
                      textKey: TextKey.Invoice,
                      value: (
                          <Typography textStyle="bodyMediumStrong" as="div">
                              {pipe(
                                  linkedInvoices,
                                  array.mapWithIndex((index, linkedInvoice) => (
                                      <EnterpriseLink
                                          key={linkedInvoice.id}
                                          variant="basic"
                                          underline={false}
                                          to="Bill/Detail/$id"
                                          params={{ id: linkedInvoice.id }}
                                      >
                                          {linkedInvoice.number}
                                      </EnterpriseLink>
                                  )),
                                  intersperse(SpacedComma)
                              )}
                          </Typography>
                      ),
                  },
              ]
            : []),
        ...getDccPaymentDetailsEntries(dccPaymentDetail),
        ...(acquirerReferenceNumber
            ? [
                  {
                      textKey: TextKey.PaymentAcquirerReferenceNumber,
                      value: (
                          <Typography textStyle="bodyMediumStrong" as="div">
                              {acquirerReferenceNumber}
                          </Typography>
                      ),
                  },
              ]
            : []),
        ...(serviceOrderNumber
            ? [
                  {
                      textKey: TextKey.ReservationNumber,
                      value: (
                          <Typography textStyle="bodyMediumStrong" as="div">
                              {serviceOrderNumber}
                          </Typography>
                      ),
                  },
              ]
            : []),
    ];
};

const getDccPaymentDetailsEntries = (dccPaymentDetail: PaymentDetails['dccPaymentDetail']) => {
    if (!dccPaymentDetail) {
        return [];
    }

    const { chargedAmount, toCurrency, fromCurrency, totalAmount } = dccPaymentDetail;
    const isPayment = chargedAmount.value > 0;

    return [
        {
            textKey: TextKey.GeneralChargedAmount,
            value: (
                <LocalizedCurrencyValue value={{ currency: chargedAmount.currencyCode, value: chargedAmount.value }} />
            ),
        },
        ...(isPayment
            ? [
                  {
                      textKey: TextKey.ExchangeRate,
                      value: <DccPaymentDetailsExchangeRateEntry fromCurrency={fromCurrency} toCurrency={toCurrency} />,
                  },
              ]
            : []),
        {
            textKey: TextKey.Amount,
            value: <ConvertedCurrencyValue amount={totalAmount} shouldShowAccountingCurrencyAmount />,
        },
    ];
};

const getDccPaymentReceiptData = (dccPaymentReceipt: DccPaymentReceiptData) => {
    const { total, transactionAmount, exchangeMarkup, fromCurrency, toCurrency, transactionMarkupPercent } =
        dccPaymentReceipt;
    const hasEcbRate = exchangeMarkup > 0;
    return [
        {
            textKey: TextKey.Total,
            value: <LocalizedCurrencyValue value={{ currency: total.currencyCode, value: total.value }} />,
        },
        {
            textKey: TextKey.GeneralChargedAmount,
            value: (
                <LocalizedCurrencyValue
                    value={{ currency: transactionAmount.currencyCode, value: transactionAmount.value }}
                />
            ),
        },
        {
            textKey: TextKey.ExchangeRate,
            value: <DccPaymentDetailsExchangeRateEntry fromCurrency={fromCurrency} toCurrency={toCurrency} />,
        },
        hasEcbRate
            ? {
                  textKey: TextKey.ExchangeRateMarkup,
                  value: (
                      <LocalizedTypography
                          textStyle="bodyMediumStrong"
                          textKey={TextKey.ExchangeRateMarkupReferenceRate}
                          substitutes={{
                              referenceRate: LocalizedPercentage({
                                  value: DecimalService.toNumber(
                                      DecimalService.divide(
                                          DecimalService.parse(exchangeMarkup),
                                          DecimalService.parse(100)
                                      )
                                  ),
                                  options: { maximumFractionDigits: 2, showWithoutSymbol: true },
                              }),
                          }}
                          as="div"
                      />
                  ),
              }
            : {
                  textKey: TextKey.ExchangeRateMarkup,
                  value: transactionMarkupPercent
                      ? LocalizedPercentage({
                            value: DecimalService.toNumber(
                                DecimalService.divide(
                                    DecimalService.parse(transactionMarkupPercent),
                                    DecimalService.parse(100)
                                )
                            ),
                            options: { maximumFractionDigits: 2 },
                        })
                      : LocalizedPercentage({ value: 0 }),
              },
    ];
};

export const getStripeReceiptEntries = (data: StripeReceiptData) => {
    const {
        amount,
        creditCardNumber,
        paymentMethod,
        chargedUtc,
        authorizationCode,
        responseCode,
        entryMode,
        terminal,
        identifier,
        name,
        address,
        postalCode,
        city,
        country,
        dccPaymentReceipt,
    } = data;

    return [
        ...(dccPaymentReceipt
            ? getDccPaymentReceiptData(dccPaymentReceipt)
            : [
                  {
                      textKey: TextKey.Amount,
                      value: <LocalizedCurrencyValue value={{ currency: amount.currency, value: amount.value }} />,
                  },
              ]),
        {
            textKey: TextKey.CreditCardNumber,
            value: (
                <Typography textStyle="bodyMediumStrong" as="div">
                    {creditCardNumber}
                </Typography>
            ),
        },
        {
            textKey: TextKey.PaymentMethod,
            value: (
                <Typography textStyle="bodyMediumStrong" as="div">
                    {paymentMethod}
                </Typography>
            ),
        },
        {
            textKey: TextKey.Date,
            value: <LocalizedDateFromUtc utcString={chargedUtc} />,
        },
        {
            textKey: TextKey.Time,
            value: <LocalizedShortTimeFromUtc utcString={chargedUtc} />,
        },
        {
            textKey: TextKey.AuthorizationCode,
            value: (
                <Typography textStyle="bodyMediumStrong" as="div">
                    {authorizationCode}
                </Typography>
            ),
        },
        {
            textKey: TextKey.ResponseCode,
            value: (
                <Typography textStyle="bodyMediumStrong" as="div">
                    {responseCode}
                </Typography>
            ),
        },
        {
            textKey: TextKey.EntryMode,
            value: (
                <Typography textStyle="bodyMediumStrong" as="div">
                    {entryMode}
                </Typography>
            ),
        },
        {
            textKey: TextKey.Terminal,
            value: (
                <Typography textStyle="bodyMediumStrong" as="div">
                    {terminal}
                </Typography>
            ),
        },
        {
            textKey: TextKey.Identifier,
            value: (
                <Typography textStyle="bodyMediumStrong" as="div">
                    {identifier}
                </Typography>
            ),
        },
        {
            textKey: TextKey.Name,
            value: (
                <Typography textStyle="bodyMediumStrong" as="div">
                    {name}
                </Typography>
            ),
        },
        {
            textKey: TextKey.Address,
            value: (
                <Typography textStyle="bodyMediumStrong" as="div">
                    {address}
                </Typography>
            ),
        },
        {
            textKey: TextKey.PostalCode,
            value: (
                <Typography textStyle="bodyMediumStrong" as="div">
                    {postalCode}
                </Typography>
            ),
        },
        {
            textKey: TextKey.City,
            value: (
                <Typography textStyle="bodyMediumStrong" as="div">
                    {city}
                </Typography>
            ),
        },
        {
            textKey: TextKey.Country,
            value: (
                <Typography textStyle="bodyMediumStrong" as="div">
                    {country}
                </Typography>
            ),
        },
    ];
};

export const getPayworksReceiptEntries = (data: PayworksReceiptData['data']) =>
    Object.entries(data).map(([key, value]) => ({ customLabel: key, value }));

export const renderDetailEntries = flow(
    array.map((entry: PaymentDetailsLocalizedEntry | PaymentDetailsCustomEntry) => (
        <PaymentDetailsEntry {...entry} key={'textKey' in entry ? entry.textKey : entry.customLabel} />
    )),
    intersperse((index: number) => <Divider key={index} />)
);

export const renderReceiptEntries = ({ discriminator, value }: Receipt['receiptData']) => {
    switch (discriminator) {
        case ReceiptType.PayworksReceipt:
            return (
                <>
                    {renderDetailEntries(getPayworksReceiptEntries(value.data))}
                    {value.signatureLineRequired && (
                        <>
                            <Divider />
                            <Typography textStyle="bodyMediumStrong" as="div">
                                <LocalizedText textKey={TextKey.Signature} />
                                {UnicodeEntity.Colon}
                            </Typography>
                        </>
                    )}
                </>
            );
        case ReceiptType.StripeReceipt:
            return renderDetailEntries(getStripeReceiptEntries(value));
        case ReceiptType.PlainReceipt:
            return value;
        default:
            return null;
    }
};
